import React, { useRef, useState, useEffect } from 'react';
import {
  Dropdown,
  Popover,
  Whisper,
  Stack,
  Badge,
  Avatar,
  IconButton,
  List,
  Button,
  Tooltip,
  Nav,
  useToaster,
  Message
} from 'rsuite';
import NoticeIcon from '@rsuite/icons/Notice';
import { useAppDispatch } from '@/config/hooks';
import { jwtDecode } from 'jwt-decode';
import { getCurrentUser, logout } from '@/services/auth.service';
import { Icon } from '@rsuite/icons';
import { MdOutlineLightMode, MdOutlineNightlight } from 'react-icons/md';
import UserIcon from '@rsuite/icons/legacy/User';
import { useNavigate } from 'react-router-dom';
import GearIcon from '@rsuite/icons/Gear';
import Brand from '@/images/wp_blue.svg';
import usFlag from '@/images/flags/gb.svg';
import frFlag from '@/images/flags/fr.svg';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';

const hexToRgb = hex => {
  let r, g, b;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `${r}, ${g}, ${b}`;
};

const renderNoticeSpeaker = ({ onClose, left, top, className }, ref) => {
  const notifications = [
    [
      '7 hours ago',
      'The charts of the dashboard have been fully upgraded and are more visually pleasing.'
    ],
    [
      '13 hours ago',
      'The function of virtualizing large lists has been added, and the style of the list can be customized as required.'
    ],
    ['2 days ago', 'Upgraded React 18 and Webpack 5.'],
    [
      '3 days ago',
      'Upgraded React Suite 5 to support TypeScript, which is more concise and efficient.'
    ]
  ];

  return (
    <Popover ref={ref} className={className} style={{ left, top, width: 300 }} title="Last updates">
      <List>
        {notifications.map((item, index) => {
          const [time, content] = item;
          return (
            <List.Item key={index}>
              <Stack spacing={4}>
                <Badge /> <span style={{ color: '#57606a' }}>{time}</span>
              </Stack>
              <p>{content}</p>
            </List.Item>
          );
        })}
      </List>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Button onClick={onClose}>More notifications</Button>
      </div>
    </Popover>
  );
};

const Header = props => {
  const {
    theme,
    onChangeTheme,
    projects,
    handleFeatureChange,
    selectedFeature,
    showFeatures,
    expand
  } = props;
  const trigger = useRef(null);
  const navigate = useNavigate();
  const languageTrigger = useRef(null);
  const { t } = useTranslation();
  const toaster = useToaster();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

  const currentUser = getCurrentUser();

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(i18n.language);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const renderAdminSpeaker = ({ onClose, left, top, className }, ref) => {
    const dispatch = useAppDispatch();

    const handleSignOut = async () => {
      dispatch(logout());
    };

    const handleProfileClick = () => {
      navigate('/myProfile');
      onClose();
    };
    console.log('currentUser', currentUser);

    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu>
          <Dropdown.Item panel style={{ padding: 10, width: 160 }}>
            <strong>{currentUser.firstname + ' ' + currentUser.lastname}</strong>
            <p>{t('Signed in as')}</p>
            <strong>{currentUser.admin} administrator</strong>
          </Dropdown.Item>
          <Dropdown.Item divider />
          <Dropdown.Item onClick={handleProfileClick}>{t('Profile & account')}</Dropdown.Item>
          <Dropdown.Item onClick={handleSignOut}>{t('Sign out')}</Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };

  const renderLanguageSpeaker = ({ onClose, left, top, className }, ref) => {
    const changeLanguage = async lang => {
      try {
        await i18n.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
        setCurrentLanguage(lang);
        toaster.push(
          <Message showIcon type="success">
            {t('Language changed to')} {lang === 'en' ? t('English') : t('French')}
          </Message>,
          { placement: 'topCenter', duration: 3000 }
        );
        onClose();
      } catch (error) {
        console.error('Error changing language:', error);
        toaster.push(
          <Message showIcon type="error">
            {t('Failed to change language')}
          </Message>,
          { placement: 'topCenter', duration: 3000 }
        );
      }
    };

    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu>
          <Dropdown.Item panel style={{ padding: 10, width: 160 }}>
            <strong>{t('Select Language')}</strong>
          </Dropdown.Item>
          <Dropdown.Item divider />
          <Dropdown.Item onSelect={() => changeLanguage('en')}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src={usFlag} alt="English" style={{ width: 20, marginRight: 8 }} />
              {t('English')}
            </div>
          </Dropdown.Item>
          <Dropdown.Item onSelect={() => changeLanguage('fr')}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src={frFlag} alt="French" style={{ width: 20, marginRight: 8 }} />
              {t('French')}
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };

  return (
    <Stack
      className="header"
      justifyContent="space-between"
      style={{
        width: expand ? 'calc(100% - 200px)' : 'calc(100% - 56px)'
      }}
    >
      {showFeatures == false ? (
        <img width={'90%'} src={Brand} />
      ) : (
        <Stack spacing={15} id="header-modules">
          {projects
            ?.filter(
              (project, index, self) =>
                index === self.findIndex(p => p.value === project.value) &&
                currentUser?.moduleAccess?.includes(project.value)
            )
            .map((item, index) => {
              return (
                <Whisper
                  key={index}
                  placement="bottom"
                  trigger="hover"
                  ref={trigger}
                  speaker={<Tooltip>{item.label}</Tooltip>}
                >
                  <IconButton
                    appearance="default"
                    size="lg"
                    circle
                    icon={item.icon}
                    onClick={() => {
                      handleFeatureChange(item.value);
                      navigate('/home');
                    }}
                    style={{
                      fontSize: 20,
                      color: selectedFeature?.value == item.value ? '' : 'var(--rs-gray-700)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      backgroundColor:
                        selectedFeature?.value == item.value ? 'rgb(13 71 161 / 30%)' : ''
                    }}
                  />
                </Whisper>
              );
            })}
        </Stack>
      )}
      <Stack spacing={8} justifyContent="center" alignItems="center">
        <Whisper
          placement="bottomEnd"
          trigger="click"
          ref={languageTrigger}
          speaker={renderLanguageSpeaker}
        >
          <Nav pullRight>
            <Button
              appearance="subtle"
              style={{
                display: 'flex',
                alignItems: 'center',
                color: 'black',
                padding: '10px 25px',
                borderRadius: 4,
                transition: 'all 0.2s ease',
                ':hover': {
                  borderRadius: '0%'
                }
              }}
            >
              <img
                src={currentLanguage === 'en' ? usFlag : frFlag}
                alt={currentLanguage === 'en' ? 'English' : 'French'}
                style={{ width: 18, marginRight: 5 }}
              />
              <span>{currentLanguage === 'en' ? 'EN' : 'FR'}</span>
            </Button>
          </Nav>
        </Whisper>
        <Whisper placement="bottomEnd" trigger="click" ref={trigger} speaker={renderAdminSpeaker}>
          <Avatar size="sm" circle style={{ marginLeft: 8 }}>
            {currentUser?.photo ? (
              <img src={process.env.REACT_APP_IMAGE_URL + currentUser?.photo} width="40" />
            ) : (
              <UserIcon />
            )}
          </Avatar>
        </Whisper>
      </Stack>
    </Stack>
  );
};

export default Header;
